import { Button as ReactButton, Label } from '@a-ui/react';
import dayjs from 'dayjs';
import React from 'react';
import { useHistory } from 'react-router-dom';
import ACardComponent from '../../components/ACard';
import Box from '../../components/Box';
import { OverviewLayout } from '../../components/DetailLayout';
import Link from '../../components/Link';
import OverviewLayoutAside from '../../components/OverviewLayout/OverviewLayoutAside';
import OverviewLayoutSection from '../../components/OverviewLayout/OverviewLayoutSection';
import Typography from '../../components/Typography';
import useConfig from '../../hooks/config.hooks';
import useDevice, { Device } from '../../hooks/device.hooks';
import { translate } from '../../i18n';
import { useAuthentication } from '../../state/Authentication';
import ACardEmpty from './ACardEmpty';
import ACardEmptyAlert from './ACardEmptyAlert';
import ACardHistory from './ACardHistory';
import ACardSummary from './ACardSummary';

const ACard: React.FC = () => {
  const {
    state: { user },
  } = useAuthentication();
  const { ACARD_SAVE_POINTS_AND_CHEQUES_URL } = useConfig();
  const device = useDevice();
  const history = useHistory();
  const isDesktop = React.useMemo(() => device === Device.DESKTOP, [device]);

  if (!user.acard) {
    return <ACardEmpty />;
  }

  return (
    <OverviewLayout title="acard.title">
      {isDesktop && (
        <OverviewLayoutAside>
          <Box>
            <ACardComponent number={user.acard.cardNumber} />
          </Box>
        </OverviewLayoutAside>
      )}

      {/* POINTS / CHEQUES */}
      <OverviewLayoutSection
        title={translate('acard.pointsCheques.title')}
        description={translate('acard.description')}
      >
        {!isDesktop && (
          <Box classNames="u-margin-bottom-xs">
            <ACardComponent number={user.acard.cardNumber} />
          </Box>
        )}

        <ACardEmptyAlert card={user.acard} />
        <ACardSummary card={user.acard} />

        <Box classNames="u-margin-top">
          <Typography variant="h6">{translate('acard.pointsCheques.save.title')}</Typography>
          <Typography classNames="u-margin-top-xs">
            {translate('acard.pointsCheques.save.body')}
          </Typography>
          <Typography classNames="u-margin-top-xs">
            <Link to={ACARD_SAVE_POINTS_AND_CHEQUES_URL}>
              {translate('acard.pointsCheques.save.link')}
            </Link>
          </Typography>
        </Box>
      </OverviewLayoutSection>

      {/* STATUTES */}
      {!!user.acard.statutes && (
        <OverviewLayoutSection
          title={translate('acard.statutes.title')}
          description={translate('acard.statutes.description')}
        >
          <div className="row">
            {user.acard?.statutes?.map((statute) => (
              <div className="col-xs-12 col-md-6" key={statute.cardholderStatuteId}>
                <Box classNames="u-margin-bottom">
                  <Label>{statute.statuteName}</Label>
                  {statute.cardholderStatuteValidTill && (
                    <Typography classNames="u-margin-top-xs">
                      <small>
                        {translate('acard.statutes.body', {
                          date: dayjs(statute.cardholderStatuteValidTill).format('DD/MM/YYYY'),
                        })}
                      </small>
                    </Typography>
                  )}
                </Box>
              </div>
            ))}
          </div>
        </OverviewLayoutSection>
      )}

      {/* BENEFITS */}
      {!!user.acard.benefits && (
        <OverviewLayoutSection
          title={translate('acard.benefits.title')}
          description={translate('acard.benefits.description')}
        >
          <Box classNames="u-margin-bottom">
            <ul className="a-list a-list--reset">
              {user.acard?.benefits?.map((benefit) => (
                <li key={benefit.accountBenefitId}>
                  <Typography variant="span" classNames="u-text-primary u-text-bold">
                    {benefit.benefitName}
                  </Typography>
                </li>
              ))}
            </ul>
          </Box>
        </OverviewLayoutSection>
      )}

      <OverviewLayoutSection title={translate('acard.transfer.title')}>
        <Box>
          <Typography>{translate('acard.transfer.body')}</Typography>
          <div className="a-button u-margin-top">
            <ReactButton onClick={() => history.push(`/a-kaart/geef-handjes-cadeau`)}>
              <Typography>{translate('acard.transfer.cta')}</Typography>
            </ReactButton>
          </div>
        </Box>
      </OverviewLayoutSection>

      <OverviewLayoutSection title={translate('acard.history.title')}>
        <ACardHistory />
      </OverviewLayoutSection>
    </OverviewLayout>
  );
};

export default ACard;
